// Importaciones de interfaces.
import { IHost } from '../interfaces/host.interface';

/**
 * Clase que representa un host.
 *
 * Esta clase contiene información relevante sobre un host, incluyendo
 * el nombre, el tipo y el identificador del host dentro de la aplicación.
 */
export class Host implements IHost {
  /**
   * Identificador único del host.
   *
   * Este campo representa un identificador único asignado al host.
   * Puede ser un número o nulo si no está asignado.
   */
  id: number | null;

  /**
   * Nombre del host.
   *
   * Este campo almacena el nombre del host.
   * Puede ser nulo si no se ha establecido.
   */
  name: string | null;

  /**
   * Tipo del host.
   *
   * Este campo define el tipo de host.
   * Puede ser nulo si no se ha establecido.
   */
  type: string | null;

  /**
   * El estado del host, indicando si está habilitado o deshabilitado.
   * Puede ser `null` si no se especifica el estado.
   */
  disabled: number;

  /**
   * Fecha de última actualización.
   *
   * Indica el momento en que el host fue modificada por última vez.
   * Es útil para el seguimiento de cambios y auditorías.
   */
  updatedAt: string | null;

  /**
   * Fecha de creación.
   *
   * Representa el instante en que el host fue registrada por primera vez.
   * Es importante para conocer su antigüedad y gestionar su validez.
   */
  createdAt: string | null;

  /**
   * Constructor de la clase que inicializa una instancia de IHost.
   *
   * @param {Partial<IHost>} host - Un objeto parcial de IHost con las siguientes propiedades opcionales:
   *   - `id` (opcional): Identificador del host. Por defecto, `null`.
   *   - `name` (opcional): Nombre del host. Por defecto, `null`.
   *   - `type` (opcional): Tipo de host. Por defecto, `null`.
   *   - `updatedAt` (opcional): Fecha de última actualización. Por defecto, `null`.
   *   - `createdAt` (opcional): Fecha de creación. Por defecto, `null`.
   */
  constructor({
    id = null,
    name = null,
    type = null,
    disabled = 0,
    updatedAt = null,
    createdAt = null,
  }: Partial<IHost> = {}) {
    this.id = id;
    this.name = name;
    this.type = type;
    this.disabled = disabled;
    this.updatedAt = updatedAt;
    this.createdAt = createdAt;
  }
}
